import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface FederationProfile {
    id: number
    name: string
    phone?: string
    fax?: string
    street?: string
    zip?: string
    city?: string
    email?: string
    homepage?: string
    executives?: string
    agent?: string
    privacyOfficer?: string
    // White-label config only: title the executives as a kindergarten's instead of a federation's.
    leadershipTitle?: 'institution'
}

export interface Organization {
    id: number
    name: string
    hasLogo: boolean
    logoApiUrl: string
    hasCustomStartImage: boolean
    customStartImageApiUrl: string
    phone?: string
    fax?: string
    street?: string
    zip?: string
    city?: string
    email?: string
    homepage?: string
    executives?: string
    agent?: string
    privacyOfficer?: string
    federationProfile?: FederationProfile
    theme?: Record<string, string>
    availableModules?: string[]
    membersCanViewInvoices?: boolean
    membersCanViewTaxCertificates?: boolean
    // Parent edit-profile gates (GET /organizations/:id/profile): members may edit their own contact
    // data; children (and the publish-settings panel) are hidden from parents.
    membersEditOwnData?: boolean
    hideChildrenFromParents?: boolean
    // Club profile fields (ClubMemberApp OrganizationProfileTransformer). The kindergarten profile
    // carries `federationProfile`/`executives`/`agent`/`privacyOfficer` instead — the two imprints
    // are built from disjoint field sets, which is why each app has its own imprint view.
    optionalContact?: string
    chiefExecutiveOfficer?: string
    chiefFinancialOfficer?: string
    chiefHumanResourcesOfficer?: string
    taxNumber?: string
    registrationCourt?: string
    companyRegistrationNumber?: string
}

export const useOrganizationStore = defineStore('organization', () => {
    // Pre-login fallback (= Angular OrganizationService `config.organization || platformOrganization`):
    // a white-label client shows its own org (incl. federationProfile + custom start image) on the
    // public imprint/contact and login screen; the generic app falls back to the KigaRoo platform
    // operator. Replaced by the real API org in initApp() once a user logs in.
    const initialOrganization
        = window.kgrDynamicConfig?.organizationProfile ?? window.kgrDynamicConfig?.platformOrganization ?? null;
    const organization = ref<Organization | null>(initialOrganization as unknown as Organization | null);

    // Angular keeps two streams apart: `organization$` (login org OR route/config fallback) and
    // `loginOrganization$` (strictly the API profile of the institution the user is logged into).
    // Anything that reads per-institution settings — `availableModules` above all — must use the
    // second one; the config fallback carries none of them, so checking it always answers "off".
    const loginOrganization = ref<Organization | null>(null);

    function setOrganization(org: Organization) {
        organization.value = org;
        loginOrganization.value = org;
    }

    // Called from `useUserStore().logout()`. Puts the ORGANIZATION DATA back to what a cold start
    // would produce, so everything the public views read off it — contact/imprint, the login start
    // image, `availableModules` — describes the client again, not the institution the user just
    // left. Angular leaks the stale institution here (its `organization$` hangs off shareReplay(1)
    // and never re-emits on logout), so this is a deliberate deviation, driven by a QA report of
    // exactly that symptom on the white-label clients.
    //
    // The institution THEME is deliberately NOT reverted — that keeps 1:1 with Angular, where the
    // unreachable `applyTheme({})` branch means the colours of the institution stay applied after a
    // logout. It was never reported, so it is not changed. Do not "fix" this without deciding it.
    function resetToPreLogin() {
        organization.value = initialOrganization as unknown as Organization | null;
        loginOrganization.value = null;
    }

    return { organization, loginOrganization, setOrganization, resetToPreLogin };
});
